import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { Sentry } from './sentry';

const isWeb = Platform.OS === 'web';

export const secureStorage = {
  async getItem(key: string): Promise<string | null> {
    if (isWeb) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.error('Error reading from sessionStorage:', error);

        Sentry.captureException(error, {
          tags: { storage_operation: 'get', platform: 'web' },
          contexts: {
            storage: { key, operation: 'getItem' },
          },
        });

        return null;
      }
    } else {
      try {
        return await SecureStore.getItemAsync(key);
      } catch (error) {
        console.error('Error reading from SecureStore:', error);

        Sentry.captureException(error, {
          tags: { storage_operation: 'get', platform: Platform.OS },
          contexts: {
            storage: { key, operation: 'getItem' },
          },
        });

        return null;
      }
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    if (isWeb) {
      try {
        sessionStorage.setItem(key, value);

        Sentry.addBreadcrumb({
          category: 'storage',
          message: 'Storage write operation completed',
          level: 'info',
        });
      } catch (error) {
        console.error('Error writing to sessionStorage:', error);

        // Check for quota exceeded error
        const isQuotaError =
          error instanceof Error &&
          (error.name === 'QuotaExceededError' || error.message?.includes('quota'));

        Sentry.captureException(error, {
          tags: {
            storage_operation: 'set',
            platform: 'web',
            quota_exceeded: isQuotaError,
          },
          contexts: {
            storage: {
              key,
              operation: 'setItem',
              valueLength: value.length,
            },
          },
          level: isQuotaError ? 'warning' : 'error',
        });

        throw error;
      }
    } else {
      try {
        await SecureStore.setItemAsync(key, value);

        Sentry.addBreadcrumb({
          category: 'storage',
          message: 'Secure storage write operation completed',
          level: 'info',
        });
      } catch (error) {
        console.error('Error writing to SecureStore:', error);

        Sentry.captureException(error, {
          tags: { storage_operation: 'set', platform: Platform.OS },
          contexts: {
            storage: {
              key,
              operation: 'setItem',
              valueLength: value.length,
            },
          },
        });

        throw error;
      }
    }
  },

  async deleteItem(key: string): Promise<void> {
    if (isWeb) {
      try {
        sessionStorage.removeItem(key);

        Sentry.addBreadcrumb({
          category: 'storage',
          message: 'Storage delete operation completed',
          level: 'info',
        });
      } catch (error) {
        console.error('Error deleting from sessionStorage:', error);

        Sentry.captureException(error, {
          tags: { storage_operation: 'delete', platform: 'web' },
          contexts: {
            storage: { key, operation: 'deleteItem' },
          },
        });
      }
    } else {
      try {
        await SecureStore.deleteItemAsync(key);

        Sentry.addBreadcrumb({
          category: 'storage',
          message: 'Secure storage delete operation completed',
          level: 'info',
        });
      } catch (error) {
        console.error('Error deleting from SecureStore:', error);

        Sentry.captureException(error, {
          tags: { storage_operation: 'delete', platform: Platform.OS },
          contexts: {
            storage: { key, operation: 'deleteItem' },
          },
        });
      }
    }
  },
};
